import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { AgentChat, Channel, ChatMessage, MediaItem, Post, Settings, SocialSet } from './types'

const GRADIENTS = [
  'linear-gradient(135deg,#6457FF,#34E5D0)',
  'linear-gradient(135deg,#FF6FB5,#FFB861)',
  'linear-gradient(135deg,#34E5D0,#B6F36B)',
  'linear-gradient(135deg,#7C7BFF,#FF6FB5)',
  'linear-gradient(135deg,#FFB861,#FF6FB5)',
  'linear-gradient(135deg,#5340F0,#34E5D0)',
]
export const pickGradient = (seed = 0) => GRADIENTS[Math.abs(seed) % GRADIENTS.length]

// Real UUIDs so store ids double as Postgres primary keys.
export const uid = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
      })

// ---------------------------------------------------------------------------
// Sync registry: data.ts registers worker-API write-through handlers here so the
// store stays free of API imports (no circular dep). Handlers are
// fire-and-forget; local state is the optimistic source of truth in-session.
// ---------------------------------------------------------------------------
export interface SyncHandlers {
  removeChannel?: (ws: string, id: string) => Promise<void>
  saveSocialSet?: (ws: string, set: SocialSet) => Promise<SocialSet>
  deleteSocialSet?: (ws: string, id: string) => Promise<void>
  upsertPost?: (ws: string, p: Post & { publishNow?: boolean }) => Promise<void>
  deletePost?: (ws: string, id: string) => Promise<void>
  addMedia?: (ws: string, m: MediaItem) => void
  removeMedia?: (ws: string, id: string) => void
  createChat?: (ws: string, c: AgentChat) => void
  deleteChat?: (ws: string, id: string) => void
  pushMessage?: (ws: string, chatId: string, m: ChatMessage) => void
}
let sync: SyncHandlers = {}
export const setSyncHandlers = (h: SyncHandlers) => {
  sync = h
}

interface AppState {
  onboarded: boolean
  activeWorkspaceId: string | null
  channels: Channel[]
  socialSets: SocialSet[]
  /** What new posts start with on this device: a set id, or `channel:<id>` for
   * an ungrouped channel. Resolved by activeSetOption, which falls back to the
   * first option when this is stale or null. */
  activeSetId: string | null
  posts: Post[]
  media: MediaItem[]
  chats: AgentChat[]
  settings: Settings

  setOnboarded: (v: boolean) => void
  setActiveWorkspaceId: (id: string | null) => void
  hydrate: (data: {
    onboardingCompleted: boolean
    channels: Channel[]
    socialSets?: SocialSet[]
    posts: Post[]
    media: MediaItem[]
    chats: AgentChat[]
  }) => void

  removeChannel: (id: string) => Promise<void>

  saveSocialSet: (set: SocialSet) => Promise<SocialSet>
  deleteSocialSet: (id: string) => Promise<void>
  setActiveSet: (id: string | null) => void

  addPost: (p: Omit<Post, 'id' | 'createdAt'>) => Post
  savePost: (
    p: Omit<Post, 'id' | 'createdAt'> & { publishNow?: boolean },
    editId?: string,
  ) => Promise<Post>
  updatePost: (id: string, patch: Partial<Post>) => void
  deletePost: (id: string) => Promise<void>
  /** Apply server-refreshed posts (poll, retry) over local state without a full reload. */
  mergePosts: (incoming: Post[]) => void

  addMedia: (m: Omit<MediaItem, 'id' | 'createdAt'>) => MediaItem
  /** Insert a server-created media item as-is (no write-through sync). */
  insertMedia: (m: MediaItem) => void
  /** Update media metadata (name doubles as alt text on the networks). */
  updateMedia: (id: string, patch: Partial<Pick<MediaItem, 'name'>>) => void
  removeMedia: (id: string) => void

  createChat: (channelIds?: string[]) => AgentChat
  deleteChat: (id: string) => void
  setChatChannels: (id: string, channelIds: string[]) => void
  pushMessage: (chatId: string, msg: ChatMessage) => void
  updateMessage: (chatId: string, msgId: string, patch: Partial<ChatMessage>) => void
  renameChat: (id: string, title: string) => void

  updateSettings: (patch: Partial<Settings>) => void
}

const defaultSettings: Settings = {
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  weekStartsMonday: true,
}

export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
      onboarded: false,
      activeWorkspaceId: null,
      channels: [],
      socialSets: [],
      activeSetId: null,
      posts: [],
      media: [],
      chats: [],
      settings: defaultSettings,

      setOnboarded: (v) => set({ onboarded: v }),
      setActiveWorkspaceId: (id) => set({ activeWorkspaceId: id }),
      hydrate: ({ onboardingCompleted, channels, socialSets = [], posts, media, chats }) =>
        set({ onboarded: onboardingCompleted, channels, socialSets, posts, media, chats }),

      removeChannel: async (id) => {
        const ws = get().activeWorkspaceId
        // Identify posts that will become orphaned (no remaining channels).
        const orphanedIds = get()
          .posts.filter((p) => p.channelIds.length === 1 && p.channelIds[0] === id)
          .map((p) => p.id)
        if (ws && sync.removeChannel) await sync.removeChannel(ws, id)
        set((s) => ({
          channels: s.channels.filter((c) => c.id !== id),
          socialSets: s.socialSets.map((x) => ({ ...x, channelIds: x.channelIds.filter((c) => c !== id) })),
          posts: s.posts
            .map((p) => ({ ...p, channelIds: p.channelIds.filter((c) => c !== id) }))
            .filter((p) => p.channelIds.length > 0),
        }))
        if (ws) {
          for (const pid of orphanedIds) await sync.deletePost?.(ws, pid)
        }
      },

      saveSocialSet: async (input) => {
        const ws = get().activeWorkspaceId
        if (!ws || !sync.saveSocialSet) throw new Error('Workspace is still loading. Try again.')
        const saved = await sync.saveSocialSet(ws, input)
        set((s) => ({
          socialSets: s.socialSets.some((x) => x.id === saved.id)
            ? s.socialSets.map((x) => (x.id === saved.id ? saved : x))
            : [...s.socialSets, saved],
        }))
        return saved
      },
      deleteSocialSet: async (id) => {
        const ws = get().activeWorkspaceId
        if (!ws || !sync.deleteSocialSet) throw new Error('Workspace is still loading. Try again.')
        await sync.deleteSocialSet(ws, id)
        set((s) => ({ socialSets: s.socialSets.filter((x) => x.id !== id) }))
      },
      setActiveSet: (id) => set({ activeSetId: id }),

      addPost: (p) => {
        const post: Post = { ...p, id: uid(), createdAt: Date.now() }
        set((s) => ({ posts: [...s.posts, post] }))
        const ws = get().activeWorkspaceId
        if (ws) void sync.upsertPost?.(ws, post).catch((e) => console.warn('[store] addPost', e))
        return post
      },
      savePost: async (input, editId) => {
        const ws = get().activeWorkspaceId
        if (!ws || !sync.upsertPost) throw new Error('Workspace is still loading. Try again.')

        const { publishNow, ...postInput } = input
        const existing = editId ? get().posts.find((post) => post.id === editId) : null
        if (editId && !existing) throw new Error('Post no longer exists. Reload and try again.')
        const post: Post = {
          ...(existing || {}),
          ...postInput,
          id: editId || uid(),
          createdAt: existing?.createdAt || Date.now(),
        } as Post

        await sync.upsertPost(ws, { ...post, publishNow })
        // The sync handler may already have merged the worker's copy of this
        // post (with fresh deliveries); keep that over the locally built one.
        set((state) => {
          const exists = state.posts.some((item) => item.id === post.id)
          return {
            posts: exists
              ? state.posts.map((item) =>
                  item.id === post.id
                    ? { ...item, ...post, deliveries: item.deliveries, updatedAt: item.updatedAt }
                    : item,
                )
              : [...state.posts, post],
          }
        })
        return get().posts.find((item) => item.id === post.id) ?? post
      },
      updatePost: (id, patch) => {
        set((s) => ({ posts: s.posts.map((p) => (p.id === id ? { ...p, ...patch } : p)) }))
        const ws = get().activeWorkspaceId
        const post = get().posts.find((p) => p.id === id)
        if (ws && post) sync.upsertPost?.(ws, post)
      },
      deletePost: async (id) => {
        const ws = get().activeWorkspaceId
        if (!ws || !sync.deletePost) throw new Error('Workspace is still loading. Try again.')
        await sync.deletePost(ws, id)
        set((s) => ({ posts: s.posts.filter((p) => p.id !== id) }))
      },
      mergePosts: (incoming) => {
        if (!incoming.length) return
        set((s) => {
          const byId = new Map(incoming.map((p) => [p.id, p]))
          const posts = s.posts.map((p) => byId.get(p.id) ?? p)
          const known = new Set(s.posts.map((p) => p.id))
          for (const p of incoming) if (!known.has(p.id)) posts.push(p)
          return { posts }
        })
      },

      addMedia: (m) => {
        const item: MediaItem = { ...m, id: uid(), createdAt: Date.now() }
        set((s) => ({ media: [item, ...s.media] }))
        const ws = get().activeWorkspaceId
        if (ws) sync.addMedia?.(ws, item)
        return item
      },
      insertMedia: (item) => set((s) => ({ media: [item, ...s.media] })),
      updateMedia: (id, patch) => {
        set((s) => ({ media: s.media.map((m) => (m.id === id ? { ...m, ...patch } : m)) }))
        const ws = get().activeWorkspaceId
        const item = get().media.find((m) => m.id === id)
        // addMedia sync handler is an upsert server-side, so it persists edits too.
        if (ws && item) sync.addMedia?.(ws, item)
      },
      removeMedia: (id) => {
        set((s) => ({ media: s.media.filter((m) => m.id !== id) }))
        const ws = get().activeWorkspaceId
        if (ws) sync.removeMedia?.(ws, id)
      },

      createChat: (channelIds = []) => {
        const chat: AgentChat = {
          id: uid(),
          title: 'New chat',
          channelIds,
          messages: [],
          createdAt: Date.now(),
        }
        set((s) => ({ chats: [chat, ...s.chats] }))
        const ws = get().activeWorkspaceId
        if (ws) sync.createChat?.(ws, chat)
        return chat
      },
      deleteChat: (id) => {
        set((s) => ({ chats: s.chats.filter((c) => c.id !== id) }))
        const ws = get().activeWorkspaceId
        if (ws) sync.deleteChat?.(ws, id)
      },
      setChatChannels: (id, channelIds) =>
        set((s) => ({ chats: s.chats.map((c) => (c.id === id ? { ...c, channelIds } : c)) })),
      pushMessage: (chatId, msg) => {
        set((s) => ({
          chats: s.chats.map((c) =>
            c.id === chatId ? { ...c, messages: [...c.messages, msg] } : c,
          ),
        }))
        const ws = get().activeWorkspaceId
        if (ws) sync.pushMessage?.(ws, chatId, msg)
      },
      updateMessage: (chatId, msgId, patch) =>
        set((s) => ({
          chats: s.chats.map((c) =>
            c.id === chatId
              ? {
                  ...c,
                  messages: c.messages.map((m) => (m.id === msgId ? { ...m, ...patch } : m)),
                }
              : c,
          ),
        })),
      renameChat: (id, title) =>
        set((s) => ({ chats: s.chats.map((c) => (c.id === id ? { ...c, title } : c)) })),

      updateSettings: (patch) => set((s) => ({ settings: { ...s.settings, ...patch } })),
    }),
    {
      name: 'hey-tasky-v1',
      // Workspace and onboarding state live in Postgres. Only device-level
      // choices stay local: settings and which social set is active.
      partialize: (s) => ({ settings: s.settings, activeSetId: s.activeSetId }),
    },
  ),
)

// ---- selectors / helpers ----
export const useChannel = (id: string) => useStore((s) => s.channels.find((c) => c.id === id))
